<template>
  <div class="rds-content-wrapper scrollbar-hidden">
    <section id="rds-section-summary">
      <h2 class="rds-section-title">{{ t('navigation.summary') }}</h2>
      <SummaryTab @navigate-tab="$emit('navigateTab', $event)" />
    </section>
    <section id="rds-section-sales" class="rds-section-divider">
      <h2 class="rds-section-title">{{ t('report.card_section.sales') }}</h2>
      <SalesTab />
    </section>
    <section id="rds-section-families" class="rds-section-divider">
      <h2 class="rds-section-title">{{ t('report.card_section.families') }}</h2>
      <FamiliesTab />
    </section>
    <section id="rds-section-clientTypes" class="rds-section-divider">
      <h2 class="rds-section-title">{{ t('report.card_section.client_types') }}</h2>
      <ClientTypesTab />
    </section>
    <section id="rds-section-clients" class="rds-section-divider">
      <h2 class="rds-section-title">{{ t('report.card_section.clients') }}</h2>
      <ClientsTab />
    </section>
    <section id="rds-section-members" class="rds-section-divider">
      <h2 class="rds-section-title">{{ t('report.card_section.members') }}</h2>
      <MembersTab />
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * Inner content for ReportDesktopShell.
 *
 * Receives centerId + srId as props and provides them via inject,
 * preserving the same contract that ReportDesktopCard established.
 * Keyed by srId in the parent so that switching reports forces a
 * clean remount with fresh provides and data fetches.
 */
import { computed, onMounted, onBeforeUnmount, provide, ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSalesReportsStore } from '../../stores/salesReports'
import { usePeriodShift } from '../../composables/usePeriodShift'
import { getSmartTimeAggregation } from '../../types/customCharts'
import { MembersApi } from '../../api/MembersApi'
import SummaryTab from './tabs/SummaryTab.vue'
import SalesTab from './tabs/SalesTab.vue'
import FamiliesTab from './tabs/FamiliesTab.vue'
import ClientTypesTab from './tabs/ClientTypesTab.vue'
import ClientsTab from './tabs/ClientsTab.vue'
import MembersTab from './tabs/MembersTab.vue'
import type { SalesReport } from '../../models/SalesReport'
import type { MembersStatsResponse } from '../../responses'

const props = defineProps<{
  centerId: number
  srId: string
}>()

const emit = defineEmits<{
  activeSection: [key: string]
  navigateTab: [key: string]
}>()

const { t } = useI18n()
const salesReportsStore = useSalesReportsStore()

// ── Report card (reactive — same getter as ReportDesktopCard) ──
const card = computed(() => {
  return salesReportsStore.getReportForCenterAndComponent(props.centerId, props.srId)
})

// ── Provide context for child tabs ──
provide('reportCard', card)
provide('reportCenterId', props.centerId)
provide('reportSrId', props.srId)

const pendingFamily = ref<string | null>(null)
provide('pendingFamily', pendingFamily)

const membersConversionData = ref<MembersStatsResponse | null>(null)
provide('reportMembersConversionData', membersConversionData)

// ── Period shift (reacts to shell arrow clicks via the model) ──
const { periodOffset, applyShift } =
  usePeriodShift(card as Ref<SalesReport | undefined>, 'rds')

// ── Expose period controls so the shell can read them ──
defineExpose({
  periodOffset,
  card,
})

// ── Scroll spy via scroll event (uses getBoundingClientRect for robustness) ──
const sectionKeys = ['summary', 'sales', 'families', 'clientTypes', 'clients', 'members']

const onScroll = () => {
  const subnav = document.querySelector('.dsn-nav')
  const threshold = subnav ? subnav.getBoundingClientRect().bottom + 16 : 140
  let activeKey = sectionKeys[0]
  for (const key of sectionKeys) {
    const el = document.getElementById(`rds-section-${key}`)
    if (el && el.getBoundingClientRect().top <= threshold) {
      activeKey = key
    }
  }
  emit('activeSection', activeKey)
}

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll, true)
})

// ── Data fetching ──
async function fetchMembersExtraData(smartAgg?: string) {
  if (!card.value) return
  if (card.value.membersConversionData) {
    membersConversionData.value = card.value.membersConversionData
    return
  }
  const interval = (smartAgg ?? card.value.interval) as any
  const resp = await MembersApi.fetchMembersStats(
    card.value.date.startDate, card.value.date.endDate,
    interval, props.centerId, ['first_activity'], ['count'],
    card.value.previousDate?.startDate, card.value.previousDate?.endDate
  )
  membersConversionData.value = resp
  card.value.membersConversionData = resp
}

async function fetchAllData() {
  if (!card.value) return

  const smartAgg = getSmartTimeAggregation(
    card.value.date.startDate.toJSDate(),
    card.value.date.endDate.toJSDate()
  )

  try {
    await Promise.all([
      card.value.fetchData(props.centerId, true, card.value.salesAggregation || undefined),
      card.value.fetchProductsReport(),
      card.value.fetchClientTypesReport(smartAgg),
      card.value.fetchClientsReport(smartAgg),
      card.value.fetchMembersReport(['recency'], ['count'], smartAgg),
      fetchMembersExtraData(smartAgg),
    ])
  } catch {
    // errors handled inside fetch methods
  }
}

watch(periodOffset, () => {
  if (!card.value) return
  if (card.value.type === 'custom') return

  applyShift()

  // Nullify cached data and re-fetch
  card.value.data = null
  card.value.productsReport = null
  card.value.clientTypesReport = null
  card.value.clientsReport = null
  card.value.membersReport = null
  card.value.membersConversionData = null
  membersConversionData.value = null

  fetchAllData()
})

onMounted(() => {
  if (card.value) {
    card.value.centerId = props.centerId
  }

  const smartAgg = card.value
    ? getSmartTimeAggregation(card.value.date.startDate.toJSDate(), card.value.date.endDate.toJSDate())
    : undefined

  // Fetch data that isn't already cached
  if (card.value?.data == null) {
    card.value?.fetchData(props.centerId, false, card.value?.salesAggregation || undefined)
  }
  if (card.value?.productsReport == null) {
    card.value?.fetchProductsReport()
  }
  if (card.value?.clientTypesReport == null) {
    card.value?.fetchClientTypesReport(smartAgg).catch(() => {})
  }
  if (card.value?.clientsReport == null) {
    card.value?.fetchClientsReport(smartAgg).catch(() => {})
  }
  if (card.value?.membersReport == null) {
    card.value?.fetchMembersReport(['recency'], ['count'], smartAgg).catch(() => {})
  }
  fetchMembersExtraData(smartAgg).catch(() => {})

  // Initialize scroll spy (capture: true to detect scroll in any container)
  window.addEventListener('scroll', onScroll, true)
})
</script>

<style scoped>
.rds-content-wrapper {
  flex: 1;
  padding: 8px 28px 20px;
  min-width: 0;
  max-width: 1200px;
}

/* Scroll offset for fixed topbar */
section[id^="rds-section-"] {
  scroll-margin-top: calc(var(--topbar-height) + 70px);
}

/* Section titles — editorial label with brand accent underline */
.rds-section-title {
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--muted-foreground);
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1.5px solid rgba(6, 200, 255, 0.25);
  display: inline-block;
}

:is(.dark *).rds-section-title {
  border-bottom-color: rgba(6, 200, 255, 0.35);
}

/* Spacing between consecutive sections */
.rds-section-divider {
  margin-top: 40px;
}
</style>
